import { useState, useEffect } from "react"

import { DEVICE_INDEX_DELIMITER } from "../../../../../components/commissioning/gt368/gt368Form"
import {
    AppError,
    parseAndThrowAppError,
} from "../../../../../errors/errorProcessors"
import { tsIoValidate } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import {
    ICxGt368FormDataParsed,
    ILaunchCxResLess368,
    IRawLaunchCxRes,
    LT_368_FORM_CLASS,
    IPostParsedLaunchCxRes,
    RawLaunchCxRes,
} from "../../../types/commissioning/installer/launchCxFormTypes"
import { LAUNCH_CX_FORM } from "../../../urls/hybrid"

interface ILaunchCxForm {
    data: IPostParsedLaunchCxRes | undefined
    isLoading: boolean
    error: AppError | null
}

const useLaunchCxForm = (applicationId: string): ILaunchCxForm => {
    const [data, setData] = useState<IPostParsedLaunchCxRes | undefined>(
        undefined
    )
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<AppError | null>(null)

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true)
            try {
                const url = LAUNCH_CX_FORM(applicationId)
                const res = await getClientProtected(url)
                const resTyped = tsIoValidate<IRawLaunchCxRes>(
                    RawLaunchCxRes,
                    res,
                    "Invalid raw launch commissioning form response"
                )

                if (resTyped.cxFormClass === LT_368_FORM_CLASS) {
                    setData(resTyped as ILaunchCxResLess368)
                } else if (!resTyped.formData) {
                    setData(resTyped as ICxGt368FormDataParsed)
                } else {
                    const { devices, ...formData } = resTyped.formData
                    const deviceData = devices.reduce(
                        (obj, device, idx) => ({
                            ...obj,
                            [`${device.deviceSysRef}${DEVICE_INDEX_DELIMITER}${idx}`]:
                                device,
                        }),
                        {}
                    )

                    setData({
                        ...resTyped,
                        formData: { ...formData, ...deviceData },
                    } as ICxGt368FormDataParsed)
                }
            } catch (err) {
                setError(parseAndThrowAppError(err))
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [applicationId])

    return {
        data,
        isLoading,
        error,
    }
}

export default useLaunchCxForm
